import axios from 'axios';
import END_POINT from '../config/endpoints.constants';
import { getErrorMessage } from '../customer-call-sdk/lib/API';
import { API_RESPONSE_CODE } from '../customer-call-sdk/config/constants';
import getRequestById from '../customer-call-sdk/api/getRequestById';
import { SwitchDeviceRow } from '../customer-call-sdk/types/call';
import { VriAPIResponse, VriAPIResult } from '../types';

/** A switch row that has been authorised and had a token issued. */
const SWITCH_STATUS_SUCCESS = 'success';

/** How recent a switch must be to count as one, in ms; generous because the server sets the timestamp. */
const RECENT_SWITCH = 60000;

/** How long a P2P call waits for the other person's new device, in ms. */
export const SWITCH_REJOIN_LIMIT = 30000;

/** Whether userId has a switch that completed within RECENT_SWITCH of now. */
export function switchedRecently(
    switches: SwitchDeviceRow[],
    userId: string,
    now: number,
): boolean {
    return switches.some(
        (row) =>
            row.userId === userId &&
            row.status === SWITCH_STATUS_SUCCESS &&
            !!row.modifiedOn &&
            Math.abs(now - new Date(row.modifiedOn).getTime()) <= RECENT_SWITCH,
    );
}

/** Whether the participant who just left is switching devices; false on any failure, so the call ends as before. */
export async function isOtherPartySwitching(requestId: string, userId: string): Promise<boolean> {
    const { data, error } = await getRequestById({ id: requestId, withSwitchDeviceDetail: true });

    if (error || !data) {
        return false;
    }

    return switchedRecently(data.callRequest.switchDeviceList ?? [], userId, Date.now());
}

export interface UserDevice {
    deviceId: string;
    type: string;
    description: string;
    deviceMake: string;
    deviceOs: string;
    lastModifiedOnText: string;
    lastModifiedOn: string;
}

export interface GetUserDevicesResponse extends VriAPIResponse {
    data: { userDeviceList: UserDevice[] };
}

export interface GetUserDevicesResult extends VriAPIResult {
    data?: UserDevice[];
}

export async function getUserDevices(): Promise<GetUserDevicesResult> {
    const result: GetUserDevicesResult = {};

    try {
        const { data: responseData } = await axios.post<GetUserDevicesResponse>(
            END_POINT.GET_USER_DEVICES,
            {},
        );
        if (responseData.code !== API_RESPONSE_CODE.SUCCESS) {
            result.error = responseData.message;
        } else if (responseData.data?.userDeviceList) {
            result.data = responseData.data.userDeviceList;
        }
    } catch (error) {
        result.error = getErrorMessage(error);
    }

    return result;
}

export interface RequestDeviceSwitchOptions {
    requestId: string;
    userId: string;
    fromDeviceId: string;
    toDeviceId: string;
}

export interface SwitchChatResult extends VriAPIResult {
    /** No response reached us, so the call is worth one retry. */
    isNetworkError?: boolean;
}

export interface GetSwitchDeviceChatKeyResponse extends VriAPIResponse {
    data?: { publicKey: string };
}

export interface GetSwitchDeviceChatKeyResult extends SwitchChatResult {
    data?: string;
}

/** The target's public key for this call's completed switch away from this device. */
export async function getSwitchDeviceChatKey(
    requestId: string,
): Promise<GetSwitchDeviceChatKeyResult> {
    const result: GetSwitchDeviceChatKeyResult = {};

    try {
        const { data: responseData } = await axios.post<GetSwitchDeviceChatKeyResponse>(
            END_POINT.GET_SWITCH_DEVICE_CHAT_KEY,
            { requestId },
        );
        if (responseData.code !== API_RESPONSE_CODE.SUCCESS || !responseData.data?.publicKey) {
            result.error = responseData.message;
        } else {
            result.data = responseData.data.publicKey;
        }
    } catch (error: any) {
        result.error = getErrorMessage(error);
        result.isNetworkError = !error?.response;
    }

    return result;
}

export interface SendSwitchDeviceChatHistoryOptions {
    requestId: string;
    /** Encrypted; see chat-crypto.util. */
    messages: string;
}

export async function sendSwitchDeviceChatHistory(
    options: SendSwitchDeviceChatHistoryOptions,
): Promise<SwitchChatResult> {
    const result: SwitchChatResult = {};

    try {
        const { data: responseData } = await axios.post<VriAPIResponse>(
            END_POINT.SEND_SWITCH_DEVICE_CHAT_HISTORY,
            options,
        );
        if (responseData.code !== API_RESPONSE_CODE.SUCCESS) {
            result.error = responseData.message;
        }
    } catch (error: any) {
        result.error = getErrorMessage(error);
        result.isNetworkError = !error?.response;
    }

    return result;
}

export async function cancelDeviceSwitch(requestId: string): Promise<VriAPIResult> {
    const result: VriAPIResult = {};

    try {
        const { data: responseData } = await axios.post<VriAPIResponse>(
            END_POINT.CANCEL_SWITCH_DEVICE_REQUEST,
            { requestId },
        );
        if (responseData.code !== API_RESPONSE_CODE.SUCCESS) {
            result.error = responseData.message;
        }
    } catch (error) {
        result.error = getErrorMessage(error);
    }

    return result;
}

export async function requestDeviceSwitch(
    options: RequestDeviceSwitchOptions,
): Promise<VriAPIResult> {
    const result: VriAPIResult = {};

    try {
        const { data: responseData } = await axios.post<VriAPIResponse>(
            END_POINT.CREATE_SWITCH_DEVICE_REQUEST,
            options,
        );
        if (responseData.code !== API_RESPONSE_CODE.SUCCESS) {
            result.error = responseData.message;
        }
    } catch (error) {
        result.error = getErrorMessage(error);
    }

    return result;
}
